import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { overlayDismiss } from '../utils/modalDismiss';

const QuizBuilder = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isEditing = !!id;
  const fileInputRef = useRef(null);

  const [quiz, setQuiz] = useState({
    title: '',
    description: '',
    time_limit_minutes: 60,
    passing_score: 50,
    allow_user_view_results: false,
    allow_review_answers: false,
    randomize_questions: false
  });
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [questionModal, setQuestionModal] = useState(null);
  const [importing, setImporting] = useState(false);
  const [categories, setCategories] = useState([]);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [pendingImage, setPendingImage] = useState(null);
  const [allQuizzes, setAllQuizzes] = useState([]);

  // Sub-categories already used in this quiz's category, offered as suggestions
  // so "APQR" is not typed three different ways.
  const subcategorySuggestions = Array.from(new Set(
    allQuizzes
      .filter(q => q.category_id === quiz.category_id && q.subcategory)
      .map(q => q.subcategory)
  )).sort();

  // Managers may pick a category when creating a quiz, but only an admin can move
  // an existing one -- the backend enforces this too; this just matches the UI to it.
  const canChangeCategory = user?.role === 'admin' || !isEditing;

  useEffect(() => {
    if (isEditing) {
      fetchQuiz();
    }
    fetchCategories();
    api.get('/quizzes').then(r => setAllQuizzes(r.data)).catch(() => {});
  }, [id]);

  const fetchCategories = async () => {
    try {
      const response = await api.get('/categories');
      setCategories(response.data);
    } catch (error) {
      // Not fatal: the quiz form still works, the picker is just empty.
      console.error('Failed to load categories', error);
    }
  };

  const fetchQuiz = async () => {
    try {
      const response = await api.get(`/quizzes/${id}`);
      const { questions: quizQuestions, ...quizData } = response.data;
      setQuiz(quizData);
      setQuestions(quizQuestions || []);
    } catch (error) {
      setError('Failed to load quiz');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveQuiz = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      if (isEditing) {
        await api.put(`/quizzes/${id}`, quiz);
      } else {
        const response = await api.post('/quizzes', quiz);
        navigate(`/quizzes/${response.data.id}`);
      }
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to save quiz');
    } finally {
      setSaving(false);
    }
  };

  const openAddQuestion = (type) => {
    setPendingImage(null);
    setQuestionModal({
      type,
      question_text: '',
      points: 1,
      randomize_options: false,
      options: type === 'multiple_choice' ? [
        { text: '', is_correct: false },
        { text: '', is_correct: false }
      ] : [],
      correct_answer: type === 'true_false' ? true : null,
      topic: '',
      explanation: '',
      trainer_note: ''
    });
  };

  const openEditQuestion = (question) => {
    setPendingImage(null);

    // Map options from database format (option_text) to modal format (text)
    const mappedOptions = question.options
      ? question.options.map(opt => ({
          text: opt.option_text || opt.text || '',
          is_correct: opt.is_correct
        }))
      : [];

    setQuestionModal({
      ...question,
      type: question.question_type,
      options: mappedOptions,
      randomize_options: question.randomize_options || false,
      image_url: question.image_url || null,
      topic: question.topic || '',
      explanation: question.explanation || '',
      trainer_note: question.trainer_note || '',
      isEdit: true
    });
  };

  const handleSaveQuestion = async () => {
    if (!questionModal.question_text.trim()) {
      alert('Please enter question text');
      return;
    }

    if (questionModal.type === 'multiple_choice') {
      const validOptions = questionModal.options.filter(o => o.text.trim());
      if (validOptions.length < 2) {
        alert('Please add at least 2 options');
        return;
      }
      if (!validOptions.some(o => o.is_correct)) {
        alert('Please select a correct answer');
        return;
      }
    }

    try {
      const payload = {
        question_text: questionModal.question_text,
        question_type: questionModal.type,
        points: questionModal.points,
        randomize_options: questionModal.type === 'multiple_choice' ? questionModal.randomize_options : false,
        options: questionModal.type === 'multiple_choice'
          ? questionModal.options.filter(o => o.text.trim())
          : undefined,
        correct_answer: questionModal.type === 'true_false'
          ? questionModal.correct_answer
          : undefined,
        topic: questionModal.topic || '',
        explanation: questionModal.explanation || '',
        trainer_note: questionModal.trainer_note || ''
      };

      if (questionModal.isEdit) {
        const response = await api.put(`/quizzes/${id}/questions/${questionModal.id}`, payload);
        // The update response has no image_url; keep whatever the modal knows so
        // the attached image does not appear to vanish until the next reload.
        setQuestions(questions.map(q => q.id === questionModal.id
          ? { ...response.data, image_url: questionModal.image_url || null }
          : q));
      } else {
        const response = await api.post(`/quizzes/${id}/questions`, payload);
        let saved = response.data;
        // The question only gets an id on save, so a file chosen beforehand is
        // uploaded now. A failure here must not lose the question itself.
        if (pendingImage) {
          try {
            saved = { ...saved, image_url: await uploadImageTo(saved.id, pendingImage) };
          } catch (imgErr) {
            alert(`Question saved, but the image failed to upload: ${imgErr.message}`);
          }
        }
        setQuestions([...questions, saved]);
      }
      setPendingImage(null);
      setQuestionModal(null);
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to save question');
    }
  };

  // POST an image to a question and return its public URL.
  const uploadImageTo = async (questionId, file) => {
    const form = new FormData();
    form.append('image', file);
    const authToken = localStorage.getItem('token');
    const response = await fetch(
      `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/quizzes/${id}/questions/${questionId}/image`,
      { method: 'POST', headers: { Authorization: `Bearer ${authToken}` }, body: form }
    );
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Upload failed');
    return data.image_url;
  };

  const handleQuestionImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingImage(true);
    try {
      const url = await uploadImageTo(questionModal.id, file);
      setQuestionModal({ ...questionModal, image_url: url });
      setQuestions(questions.map(q => q.id === questionModal.id ? { ...q, image_url: url } : q));
    } catch (err) {
      alert(err.message || 'Failed to upload image');
    } finally {
      setUploadingImage(false);
      e.target.value = '';
    }
  };

  const handleRemoveQuestionImage = async () => {
    if (!window.confirm('Remove the image from this question?')) return;
    setUploadingImage(true);
    try {
      await api.delete(`/quizzes/${id}/questions/${questionModal.id}/image`);
      setQuestionModal({ ...questionModal, image_url: null });
      setQuestions(questions.map(q => q.id === questionModal.id ? { ...q, image_url: null } : q));
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to remove image');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleDeleteQuestion = async (questionId) => {
    if (!window.confirm('Are you sure you want to delete this question?')) return;

    try {
      await api.delete(`/quizzes/${id}/questions/${questionId}`);
      setQuestions(questions.filter(q => q.id !== questionId));
    } catch (error) {
      alert('Failed to delete question');
    }
  };

  const handleDownloadTemplate = () => {
    const token = localStorage.getItem('token');
    if (!token) {
      alert('Please login to download template');
      return;
    }

    fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/quizzes/template/download`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
    .then(response => {
      if (!response.ok) throw new Error('Download failed');
      return response.blob();
    })
    .then(blob => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'quiz_import_template.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    })
    .catch(error => {
      console.error('Template download error:', error);
      alert('Failed to download template');
    });
  };

  const handleExportQuiz = () => {
    const token = localStorage.getItem('token');
    if (!token) {
      alert('Please login to export quiz');
      return;
    }

    fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/quizzes/${id}/export`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
    .then(response => {
      if (!response.ok) throw new Error('Export failed');
      return response.blob();
    })
    .then(blob => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${quiz.title.replace(/[^a-zA-Z0-9]/g, '_')}_questions.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    })
    .catch(error => {
      console.error('Export error:', error);
      alert('Failed to export quiz');
    });
  };

  const handleImportQuestions = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setImporting(true);
    setError('');

    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/quizzes/${id}/import`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        }
      );

      const data = await response.json();

      if (!response.ok) {
        // A rejected import lists exactly which rows could not be read, and
        // nothing was added. Show all of it rather than just the summary line.
        const detail = Array.isArray(data.errors) && data.errors.length
          ? `${data.error}\n\n${data.errors.join('\n')}`
          : (data.error || 'Import failed');
        throw new Error(detail);
      }

      alert(data.message);
      fetchQuiz(); // Refresh questions
    } catch (error) {
      console.error('Import error:', error);
      setError(error.message || 'Failed to import questions');
    } finally {
      setImporting(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const addOption = () => {
    setQuestionModal({
      ...questionModal,
      options: [...questionModal.options, { text: '', is_correct: false }]
    });
  };

  const removeOption = (index) => {
    setQuestionModal({
      ...questionModal,
      options: questionModal.options.filter((_, i) => i !== index)
    });
  };

  const updateOption = (index, field, value) => {
    const newOptions = [...questionModal.options];
    if (field === 'is_correct' && value) {
      // Only one correct answer
      newOptions.forEach((o, i) => o.is_correct = i === index);
    } else {
      newOptions[index][field] = value;
    }
    setQuestionModal({ ...questionModal, options: newOptions });
  };

  if (loading) {
    return <div className="text-center mt-5">Loading...</div>;
  }

  return (
    <div>
      <div className="flex flex-between flex-center mb-5">
        <h1>{isEditing ? 'Edit Quiz' : 'Create New Quiz'}</h1>
        <button className="btn btn-secondary" onClick={() => navigate('/quizzes')}>
          Back to Quizzes
        </button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {/* Quiz Details */}
      <div className="card mb-4">
        <h3 className="mb-4">Quiz Details</h3>
        <form onSubmit={handleSaveQuiz}>
          <div className="grid grid-2">
            <div className="form-group">
              <label>Title *</label>
              <input
                type="text"
                value={quiz.title}
                onChange={e => setQuiz({ ...quiz, title: e.target.value })}
                placeholder="Enter quiz title"
                required
              />
            </div>
            <div className="form-group">
              <label>Time Limit (minutes)</label>
              <input
                type="number"
                value={quiz.time_limit_minutes}
                onChange={e => setQuiz({ ...quiz, time_limit_minutes: parseInt(e.target.value) })}
                min="1"
              />
            </div>
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea
              value={quiz.description || ''}
              onChange={e => setQuiz({ ...quiz, description: e.target.value })}
              placeholder="Enter quiz description (optional)"
              rows="3"
            />
          </div>

          <div className="form-group">
            <label>Category</label>
            <select
              value={quiz.category_id || ''}
              onChange={e => setQuiz({ ...quiz, category_id: e.target.value ? parseInt(e.target.value, 10) : null, subcategory: '' })}
              disabled={!canChangeCategory}
            >
              <option value="">No category — administrators only</option>
              {categories.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            <p className="text-sm text-muted" style={{ marginTop: '6px' }}>
              {canChangeCategory
                ? 'Anyone managing this category can run this exam and see its results.'
                : 'Only an administrator can move an exam between categories.'}
            </p>
          </div>

          {/* A grouping label inside the category (e.g. CPV, APQR). It changes
              nothing about who can reach the exam. */}
          {quiz.category_id && (
            <div className="form-group">
              <label>Sub-category (optional)</label>
              <input
                type="text"
                list="subcategory-suggestions"
                maxLength={100}
                value={quiz.subcategory || ''}
                onChange={e => setQuiz({ ...quiz, subcategory: e.target.value })}
                placeholder="e.g. APQR"
              />
              <datalist id="subcategory-suggestions">
                {subcategorySuggestions.map(s => <option key={s} value={s} />)}
              </datalist>
              <p className="text-sm text-muted" style={{ marginTop: '6px' }}>
                Groups exams within the category. Pick an existing one or type a new name.
              </p>
            </div>
          )}

          <div className="grid grid-2">
            <div className="form-group">
              <label>Passing Score (%)</label>
              <input
                type="number"
                value={quiz.passing_score}
                onChange={e => setQuiz({ ...quiz, passing_score: parseInt(e.target.value) })}
                min="0"
                max="100"
              />
            </div>
            <div className="form-group">
              <label>Allow Candidates to View Results</label>
              <select
                value={quiz.allow_user_view_results ? 'yes' : 'no'}
                onChange={e => setQuiz({ ...quiz, allow_user_view_results: e.target.value === 'yes' })}
              >
                <option value="no">No - Admin/Examiner only</option>
                <option value="yes">Yes - Show results to candidates</option>
              </select>
            </div>
          </div>

          <div className="grid grid-2">
            <div className="form-group">
              <label>Allow Candidates to Review Answers</label>
              <select
                value={quiz.allow_review_answers ? 'yes' : 'no'}
                onChange={e => setQuiz({ ...quiz, allow_review_answers: e.target.value === 'yes' })}
              >
                <option value="no">No - Hide correct answers</option>
                <option value="yes">Yes - Show correct answers after submission</option>
              </select>
              <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                When enabled, candidates can review their answers and see correct answers after completing the exam
              </p>
            </div>

            {/* Only meaningful once review is on. */}
            {quiz.allow_review_answers && (
              <div className="form-group">
                <label>When to Reveal Answers</label>
                <select
                  value={quiz.review_timing || 'immediate'}
                  onChange={e => setQuiz({ ...quiz, review_timing: e.target.value })}
                >
                  <option value="immediate">Straight after each candidate submits</option>
                  <option value="after_session">Only after the exam window closes</option>
                </select>
                <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                  Holding answers until the window closes stops early finishers passing them to
                  colleagues still sitting the exam. Better for graded exams; immediate suits awareness drills.
                </p>
              </div>
            )}
          </div>

          {quiz.allow_review_answers && (
            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={!!quiz.show_trainer_notes}
                  onChange={e => setQuiz({ ...quiz, show_trainer_notes: e.target.checked })}
                  style={{ width: '18px', height: '18px' }}
                />
                Show trainer notes to candidates in the review
              </label>
              <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px', marginLeft: '28px' }}>
                Off by default. Explanations are always shown; trainer notes ("why the other options
                fail") stay visible to trainers only unless you tick this.
              </p>
            </div>
          )}

          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={quiz.randomize_questions || false}
                onChange={e => setQuiz({ ...quiz, randomize_questions: e.target.checked })}
                style={{ width: '18px', height: '18px' }}
              />
              <span>Randomize question order for each candidate</span>
            </label>
            <p style={{ fontSize: '13px', color: '#6b7280', marginTop: '4px', marginLeft: '28px' }}>
              Each candidate will see questions in a different random order
            </p>
          </div>

          <div className="flex gap-3" style={{ marginTop: '20px' }}>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving...' : 'Save Quiz Details'}
            </button>
            {isEditing && (
              <div className="flex flex-center gap-2">
                <span className="text-muted">Access Code:</span>
                <code style={{ background: '#f3f4f6', padding: '8px 16px', borderRadius: '4px', fontSize: '16px' }}>
                  {quiz.access_code}
                </code>
              </div>
            )}
          </div>
        </form>
      </div>

      {/* Questions Section - Only show if quiz is saved */}
      {isEditing && (
        <div className="card">
          <div className="flex flex-between flex-center mb-4">
            <h3>Questions ({questions.length})</h3>
            <div className="flex gap-2">
              <button className="btn btn-primary btn-sm" onClick={() => openAddQuestion('multiple_choice')}>
                + Multiple Choice
              </button>
              <button className="btn btn-success btn-sm" onClick={() => openAddQuestion('true_false')}>
                + True/False
              </button>
              <button className="btn btn-outline btn-sm" onClick={() => openAddQuestion('free_text')}>
                + Free Text
              </button>
            </div>
          </div>

          {/* Import/Export Section */}
          <div style={{
            display: 'flex',
            gap: '12px',
            marginBottom: '20px',
            padding: '16px',
            backgroundColor: '#f9fafb',
            borderRadius: '8px',
            border: '1px solid #e5e7eb',
            alignItems: 'center',
            flexWrap: 'wrap'
          }}>
            <span style={{ fontWeight: '500', color: '#374151' }}>Bulk Actions:</span>
            <button
              className="btn btn-secondary btn-sm"
              onClick={handleDownloadTemplate}
              style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
            >
              Download Template
            </button>
            <label
              className="btn btn-secondary btn-sm"
              style={{ cursor: importing ? 'wait' : 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
            >
              {importing ? 'Importing...' : 'Import CSV'}
              <input
                type="file"
                accept=".csv"
                onChange={handleImportQuestions}
                disabled={importing}
                ref={fileInputRef}
                style={{ display: 'none' }}
              />
            </label>
            {questions.length > 0 && (
              <button
                className="btn btn-secondary btn-sm"
                onClick={handleExportQuiz}
                style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
              >
                Export Quiz
              </button>
            )}
          </div>

          {questions.length === 0 ? (
            <div className="empty-state">
              <p>No questions yet. Add your first question using the buttons above.</p>
            </div>
          ) : (
            <div>
              {questions.map((q, index) => (
                <div key={q.id} style={{
                  padding: '16px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  marginBottom: '12px'
                }}>
                  <div className="flex flex-between flex-center">
                    <div style={{ flex: 1 }}>
                      <span className="badge badge-info" style={{ marginRight: '8px' }}>
                        {q.question_type === 'multiple_choice' ? 'MCQ' :
                         q.question_type === 'true_false' ? 'T/F' : 'Text'}
                      </span>
                      <span className="badge badge-warning">{q.points} pt{q.points > 1 ? 's' : ''}</span>
                      {q.image_url && (
                        <span className="badge" style={{ backgroundColor: '#ede9fe', color: '#5b21b6', fontSize: '11px', marginLeft: '8px' }}>
                          Image
                        </span>
                      )}
                      {q.topic && (
                        <span className="badge" style={{ backgroundColor: '#e0f2fe', color: '#075985', fontSize: '11px', marginLeft: '8px' }}>
                          {q.topic}
                        </span>
                      )}
                      {q.explanation && (
                        <span className="badge" style={{ backgroundColor: '#dbeafe', color: '#1d4ed8', fontSize: '11px', marginLeft: '8px' }}>
                          Explained
                        </span>
                      )}
                      <p style={{ marginTop: '8px', fontWeight: '500' }}>
                        {index + 1}. {q.question_text}
                      </p>
                      {/* Shown here as well as in the editor -- otherwise there is
                          no way to tell a question has a figure without opening it. */}
                      {q.image_url && (
                        <img
                          src={q.image_url}
                          alt="Figure for this question"
                          style={{
                            display: 'block',
                            maxWidth: '100%',
                            maxHeight: '200px',
                            margin: '10px 0',
                            border: '1px solid #e5e7eb',
                            borderRadius: '6px',
                            padding: '4px',
                            background: 'white'
                          }}
                        />
                      )}
                      {q.question_type === 'multiple_choice' && q.options && (
                        <>
                          {q.randomize_options && (
                            <span className="badge" style={{ backgroundColor: '#dbeafe', color: '#1d4ed8', fontSize: '11px', marginLeft: '8px' }}>
                              Shuffled
                            </span>
                          )}
                          <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
                            {q.options.map((opt, i) => (
                              <li key={i} style={{ color: opt.is_correct ? '#059669' : '#6b7280' }}>
                                {opt.option_text} {opt.is_correct && '✓'}
                              </li>
                            ))}
                          </ul>
                        </>
                      )}
                      {q.question_type === 'true_false' && (
                        <p className="text-muted text-sm mt-2">
                          Correct answer: {q.correct_answer ? 'True' : 'False'}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <button className="btn btn-secondary btn-sm" onClick={() => openEditQuestion(q)}>
                        Edit
                      </button>
                      <button className="btn btn-danger btn-sm" onClick={() => handleDeleteQuestion(q.id)}>
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Question Modal */}
      {questionModal && (
        <div className="modal-overlay" {...overlayDismiss(() => setQuestionModal(null))}>
          <div className="modal" style={{ maxWidth: '600px' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                {questionModal.isEdit ? 'Edit' : 'Add'} {' '}
                {questionModal.type === 'multiple_choice' ? 'Multiple Choice' :
                 questionModal.type === 'true_false' ? 'True/False' : 'Free Text'} Question
              </h3>
              <button className="modal-close" onClick={() => setQuestionModal(null)}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Question Text *</label>
                <textarea
                  value={questionModal.question_text}
                  onChange={e => setQuestionModal({ ...questionModal, question_text: e.target.value })}
                  placeholder="Enter your question"
                  rows="3"
                />
              </div>

              {/* Image. For a new question there is no id to attach to yet, so the
                  file is held and uploaded immediately after the question saves. */}
              <div className="form-group">
                <label>Image (optional)</label>
                {!questionModal.isEdit ? (
                  <div>
                    {pendingImage ? (
                      <div style={{ marginBottom: '10px' }}>
                        <img
                          src={URL.createObjectURL(pendingImage)}
                          alt="Selected figure"
                          style={{ maxWidth: '100%', maxHeight: '200px', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '4px' }}
                        />
                        <div style={{ marginTop: '8px' }}>
                          <span className="text-sm text-muted">{pendingImage.name} </span>
                          <button type="button" className="btn btn-sm btn-secondary"
                            onClick={() => setPendingImage(null)}>
                            Clear
                          </button>
                        </div>
                      </div>
                    ) : null}
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/gif,image/webp"
                      onChange={e => {
                        const f = e.target.files[0];
                        if (!f) return;
                        if (f.size > 2 * 1024 * 1024) {
                          alert('Image must be 2 MB or smaller');
                          e.target.value = '';
                          return;
                        }
                        setPendingImage(f);
                      }}
                    />
                    <p className="text-sm text-muted" style={{ marginTop: '6px' }}>
                      PNG, JPEG, GIF or WebP, up to 2 MB. Uploaded when you add the question.
                    </p>
                  </div>
                ) : (
                  <div>
                    {questionModal.image_url ? (
                      <div style={{ marginBottom: '10px' }}>
                        <img
                          src={questionModal.image_url}
                          alt="Attached figure"
                          style={{ maxWidth: '100%', maxHeight: '220px', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '4px' }}
                        />
                        <div style={{ marginTop: '8px' }}>
                          <button type="button" className="btn btn-sm btn-danger"
                            onClick={handleRemoveQuestionImage} disabled={uploadingImage}>
                            Remove image
                          </button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-muted text-sm" style={{ marginBottom: '8px' }}>
                        No image attached.
                      </p>
                    )}
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/gif,image/webp"
                      onChange={handleQuestionImageUpload}
                      disabled={uploadingImage}
                    />
                    {uploadingImage && <span className="text-sm text-muted"> uploading...</span>}
                    <p className="text-sm text-muted" style={{ marginTop: '6px' }}>
                      PNG, JPEG, GIF or WebP, up to 2 MB. Shown to candidates below the question text.
                    </p>
                  </div>
                )}
              </div>

              <div className="form-group">
                <label>Points</label>
                <input
                  type="number"
                  value={questionModal.points}
                  onChange={e => setQuestionModal({ ...questionModal, points: parseInt(e.target.value) || 1 })}
                  min="1"
                />
              </div>

              {questionModal.type === 'multiple_choice' && (
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f0f9ff', borderRadius: '8px', border: '1px solid #bae6fd' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={questionModal.randomize_options || false}
                        onChange={e => setQuestionModal({ ...questionModal, randomize_options: e.target.checked })}
                        style={{ width: '18px', height: '18px' }}
                      />
                      <span style={{ fontWeight: '500' }}>Randomize answer options</span>
                    </label>
                    <p style={{ fontSize: '12px', color: '#0369a1', marginTop: '4px', marginLeft: '28px' }}>
                      Each candidate will see options in a different random order
                    </p>
                  </div>
                  <label style={{ display: 'block', marginBottom: '10px', fontWeight: '500', color: '#374151' }}>
                    Options (select the correct answer)
                  </label>
                  {questionModal.options.map((opt, index) => (
                    <div key={index} style={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      marginBottom: '10px',
                      padding: '10px',
                      backgroundColor: opt.is_correct ? '#f0fdf4' : '#f9fafb',
                      borderRadius: '8px',
                      border: opt.is_correct ? '2px solid #10b981' : '1px solid #e5e7eb'
                    }}>
                      <input
                        type="radio"
                        name="correct_option"
                        checked={opt.is_correct}
                        onChange={() => updateOption(index, 'is_correct', true)}
                        style={{
                          width: '20px',
                          height: '20px',
                          marginRight: '12px',
                          cursor: 'pointer',
                          flexShrink: 0
                        }}
                      />
                      <input
                        type="text"
                        value={opt.text}
                        onChange={e => updateOption(index, 'text', e.target.value)}
                        placeholder={`Enter option ${index + 1}`}
                        style={{
                          flex: 1,
                          padding: '12px',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          fontSize: '14px',
                          backgroundColor: 'white',
                          marginRight: '10px',
                          minWidth: '0'
                        }}
                      />
                      {questionModal.options.length > 2 && (
                        <button
                          type="button"
                          onClick={() => removeOption(index)}
                          style={{
                            padding: '8px 12px',
                            backgroundColor: '#ef4444',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            flexShrink: 0
                          }}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addOption}
                    style={{
                      marginTop: '10px',
                      padding: '10px 16px',
                      backgroundColor: '#f97316',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    + Add Another Option
                  </button>
                </div>
              )}

              {questionModal.type === 'true_false' && (
                <div className="form-group">
                  <label>Correct Answer</label>
                  <div className="flex gap-4">
                    <label className="flex flex-center gap-2">
                      <input
                        type="radio"
                        name="tf_answer"
                        checked={questionModal.correct_answer === true}
                        onChange={() => setQuestionModal({ ...questionModal, correct_answer: true })}
                      />
                      True
                    </label>
                    <label className="flex flex-center gap-2">
                      <input
                        type="radio"
                        name="tf_answer"
                        checked={questionModal.correct_answer === false}
                        onChange={() => setQuestionModal({ ...questionModal, correct_answer: false })}
                      />
                      False
                    </label>
                  </div>
                </div>
              )}

              {questionModal.type === 'free_text' && (
                <div className="alert alert-info">
                  Free text questions require manual grading by the examiner.
                </div>
              )}

              {/* Feedback shown after the exam. */}
              <div className="form-group" style={{ marginTop: '18px' }}>
                <label>Topic (optional)</label>
                <input
                  type="text"
                  maxLength={100}
                  value={questionModal.topic || ''}
                  onChange={e => setQuestionModal({ ...questionModal, topic: e.target.value })}
                  placeholder="e.g. Phishing"
                />
              </div>

              <div className="form-group">
                <label>Explanation (optional)</label>
                <textarea
                  rows="4"
                  value={questionModal.explanation || ''}
                  onChange={e => setQuestionModal({ ...questionModal, explanation: e.target.value })}
                  placeholder="Why the correct answer is correct. Shown to candidates when they review their answers."
                />
              </div>

              <div className="form-group">
                <label>Trainer note (optional)</label>
                <textarea
                  rows="3"
                  value={questionModal.trainer_note || ''}
                  onChange={e => setQuestionModal({ ...questionModal, trainer_note: e.target.value })}
                  placeholder="Why the other options fail -- for discussion after the quiz."
                />
                <p className="text-sm text-muted" style={{ marginTop: '6px' }}>
                  Visible to trainers. Candidates see it only if the quiz has
                  &ldquo;Show trainer notes to candidates&rdquo; ticked.
                </p>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setQuestionModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSaveQuestion}>
                {questionModal.isEdit ? 'Update' : 'Add'} Question
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuizBuilder;
